import React from 'react';
import { Outlet, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const PublicLayout = () => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const { user } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const menuItems = [
    { label: 'Accueil', path: '/' },
    { label: 'Blog', path: '/blog' },
    { label: '🌳 Généalogie', path: '/genealogie' },
    { label: '📋 Annuaire', path: '/annuaire' },
    { label: '🛍️ Coin Affaires', path: '/coin-affaires' },
    { label: 'À propos / Contact', path: '/about' }
  ];

  const drawer = (
    <Box sx={{ width: 250, pt: 2 }}>
      <Box display="flex" justifyContent="flex-end" px={2}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.path}
            component={RouterLink}
            to={item.path}
            onClick={handleDrawerToggle}
            selected={location.pathname === item.path}
            sx={{
              color: 'text.primary',
              textDecoration: 'none',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.main',
                }
              }
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        <ListItem>
          {user ? (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              component={RouterLink}
              to={user.role === 'membre' ? '/member/dashboard' : '/admin/dashboard'}
              onClick={handleDrawerToggle}
            >
              Mon Espace
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/login"
              onClick={handleDrawerToggle}
            >
              Connexion
            </Button>
          )}
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar 
        position="sticky" 
        elevation={1}
        sx={{
          backgroundColor: 'white',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            {/* Logo */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
                mr: 4
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Logo"
                sx={{
                  height: 40,
                  mr: 2
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                BON'EPEY'A NTOCKO
              </Typography>
            </Box>

            {/* Desktop Menu */}
            {!isMobile && (
              <>
                <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
                  {menuItems.map((item) => (
                    <Button
                      key={item.path}
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        color: location.pathname === item.path 
                          ? 'primary.main' 
                          : 'text.primary',
                        fontWeight: location.pathname === item.path 
                          ? 600 
                          : 400,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>

                {/* Auth Buttons */}
                <Box>
                  {user ? (
                    <Button
                      variant="contained"
                      color="primary"
                      component={RouterLink}
                      to={user.role === 'membre' ? '/member/dashboard' : '/admin/dashboard'}
                      sx={{ borderRadius: 2 }}
                    >
                      Mon Espace
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        component={RouterLink}
                        to="/register"
                        sx={{ borderRadius: 2 }}
                      >
                        S'inscrire
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        component={RouterLink}
                        to="/login"
                        sx={{ borderRadius: 2 }}
                      >
                        Connexion
                      </Button>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Mobile Menu Icon */}
            {isMobile && (
              <>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default' }}>
        <Outlet />
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          backgroundColor: 'primary.dark',
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Typography variant="body2">
              © {new Date().getFullYear()} BON'EPEY'A NTOCKO. Tous droits réservés.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: 3, rowGap: 1 }}>
              <Typography
                variant="body2"
                component={RouterLink}
                to="/privacy"
                sx={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Confidentialité
              </Typography>
              <Typography
                variant="body2"
                component={RouterLink}
                to="/terms"
                sx={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Conditions
              </Typography>
              <Typography
                variant="body2"
                component={RouterLink}
                to="/genealogie"
                sx={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Généalogie
              </Typography>
              <Typography
                variant="body2"
                component={RouterLink}
                to="/annuaire"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Annuaire
              </Typography>
              <Typography
                variant="body2"
                component={RouterLink}
                to="/coin-affaires"
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Coin Affaires
              </Typography>
              <Typography
                variant="body2"
                component={RouterLink}
                to="/help"
                sx={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Aide
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicLayout;
